import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ProductionDashboard from './dashboards/ProductionDashboard';
import { dashboardService } from '../services';
import DevicePicker from '../components/DevicePicker';
import './DashboardView.css';

// Which machine the operator was last looking at, per dashboard, so a browser
// refresh (or coming back tomorrow) reopens on it instead of snapping back to
// the starred device.
const selectionKey = (dashboardId) => `phdash.selectedDevice.${dashboardId}`;

// localStorage throws when storage is disabled or full; a remembered machine is
// a convenience, so never let it break loading the dashboard.
const readRememberedDeviceId = (dashboardId) => {
  try {
    return localStorage.getItem(selectionKey(dashboardId));
  } catch (e) {
    return null;
  }
};

const rememberDeviceId = (dashboardId, device) => {
  try {
    if (device) localStorage.setItem(selectionKey(dashboardId), String(device.id));
    else localStorage.removeItem(selectionKey(dashboardId));
  } catch (e) {
    // ignore -- selection still works for this session
  }
};

const DashboardView = () => {
  const { dashboardId } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, [dashboardId]);

  // Online/offline comes from ThingsBoard's active flag, fetched with the list,
  // so refresh it periodically -- otherwise a machine that drops offline stays
  // in the Online group until the page is reloaded.
  useEffect(() => {
    if (!dashboard || !dashboard.userConfig || !dashboard.userConfig.id) return undefined;
    const t = setInterval(() => loadDevices(dashboard.userConfig.id), 60000);
    return () => clearInterval(t);
  }, [dashboard]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectDevice = (device) => {
    setSelectedDevice(device);
    rememberDeviceId(dashboardId, device);
  };

  const loadDashboard = async () => {
    try {
      const data = await dashboardService.getById(dashboardId);
      setDashboard(data);
      
      // Load devices if user has dashboard assignment
      if (data.userConfig && data.userConfig.id) {
        await loadDevices(data.userConfig.id);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setError('Failed to load dashboard: ' + (error.response?.data?.error || error.message));
      setLoading(false);
    }
  };

  const loadDevices = async (userDashboardId) => {
    try {
      const response = await fetch(`/api/user-dashboards/${userDashboardId}/devices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const deviceList = await response.json();
        setDevices(deviceList);

        // Keep whatever is selected across list refreshes. On the first load
        // there is nothing selected yet, so restore the machine this browser
        // was last on; only fall back to the starred device (even if offline --
        // predictable beats convenient) when that machine is gone or unknown.
        setSelectedDevice((current) => {
          if (current) {
            const same = deviceList.find((d) => d.id === current.id);
            if (same) return same;
          }
          const rememberedId = readRememberedDeviceId(dashboardId);
          if (rememberedId) {
            const remembered = deviceList.find((d) => String(d.id) === rememberedId);
            if (remembered) return remembered;
          }
          return deviceList.find((d) => d.is_default) || deviceList[0] || null;
        });
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-view-page">
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard {dashboardId}...</p>
          <small style={{ marginTop: '10px', color: '#888' }}>
            If this takes more than 5 seconds, check browser console (F12)
          </small>
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="dashboard-view-page">
        <Navbar />
        <div className="error-container">
          <h2>Dashboard Not Found</h2>
          <p>{error || 'The requested dashboard could not be found.'}</p>
          <button onClick={() => window.location.href = '/dashboards'}>
            ← Back to Dashboards
          </button>
        </div>
      </div>
    );
  }

  // Route to specific dashboard component based on dashboard_id
  const renderDashboard = () => {
    const dashboardProps = {
      dashboard: dashboard,
      selectedDevice
    };

    switch (dashboard.dashboard_id) {
      case 'phdashb001':
        return <ProductionDashboard {...dashboardProps} />;
      default:
        return (
          <div className="default-dashboard">
            <h2>{dashboard.name}</h2>
            <p>{dashboard.description}</p>
            <div className="dashboard-placeholder">
              Custom dashboard UI for {dashboard.dashboard_id} to be implemented
            </div>
          </div>
        );
    }
  };

  return (
    <div className="dashboard-view-page">
      <Navbar />
      
      {/* Device selector - shown when devices are assigned */}
      {devices.length > 0 && (
        <div className="device-selector-container">
          <label>
            <span className="device-selector-icon">📱</span>
            Machine:
          </label>
          <DevicePicker
            devices={devices}
            selectedDevice={selectedDevice}
            onSelect={handleSelectDevice}
          />
          <span className="device-count">{devices.length} machine{devices.length !== 1 ? 's' : ''}</span>
        </div>
      )}
      
      {dashboard ? renderDashboard() : <div>Loading dashboard data...</div>}
    </div>
  );
};

export default DashboardView;
